import React, { PureComponent } from "react";
import styled from 'styled-components';
import { Loader } from "react-loaders";
import DateTimePicker from 'react-datetime';

const Container = styled.div`
  width: 100%;
  margin-top: 10px;
  font-family: 'Mukta Vaani';
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const HeaderSpacer = styled.div`
  width: 100px;
  flex-shrink: 0;
`;

const HeaderLabel = styled.span`
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  color: rgba(0, 27, 59, 0.94);
  flex: 1;
  padding: 0 20px;
`;

const HeaderShowResults = styled.span`
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  color: rgba(0, 27, 59, 0.94);
  width: 80px;
  text-align: center;
  flex-shrink: 0;
`;

const HeaderRoundWeight = styled.span`
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  color: rgba(0, 27, 59, 0.94);
  width: 100px;
  text-align: center;
  flex-shrink: 0;
`;

const HeaderUpdateSpacer = styled.div`
  width: 78px;
  flex-shrink: 0;
`;

const RoundWeightCell = styled.div`
  width: 100px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
`;

const RoundWeightInput = styled.input`
  width: 84px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #D5D9DD;
  border-radius: 4px;
  font-family: 'Mukta Vaani';
  font-size: 14px;
  color: rgba(0, 27, 59, 0.94);
  text-align: center;

  &:focus {
    outline: none;
    border-color: #2196F3;
  }

  &:disabled {
    background: #F5F6F7;
    color: rgba(0, 27, 59, 0.38);
  }
`;

const RoundWeightPlaceholder = styled.div`
  width: 84px;
  height: 32px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const RoundNameCell = styled.div`
  width: 100px;
  flex-shrink: 0;
`;

const RoundName = styled.span`
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.38);
`;

const RoundDateTimeCell = styled.div`
  flex: 1;

  ${p => p.disabled && `
    opacity: 0.4;
    pointer-events: none;
  `}
`;

const StartGapWarning = styled.div`
  margin-top: 4px;
  font-size: 16px;
  line-height: 20px;
  color: #c74234;
`;

const RoundUpdateButton = styled.button`
  background: #F5F6F7;
  border-radius: 4px;
  padding: 6.5px 9.5px;
  color: rgba(0, 27, 59, 0.38);
  font-weight: 500;
  font-size: 16px;
  line-height: 16px;
  pointer-events: none;
  min-width: 78px;
  flex-shrink: 0;

  ${p => p.isActive && `
    background: #2196F3;
    color: #fff;
    pointer-events: auto;
  `}
  ${p => p.isActiveDefault && `
    background: #FF5252;
  `}
`;

const ButtonLoader = styled.div`
  width: 100%;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  > .loader {
    margin-top: 0 !important;
    margin-left: 0 !important;

    .loader-inner div {
      width: 15px;
      height: 15px;
      border-color: #fff !important;
    }
  }
`;

const RoundShowResultsCell = styled.div`
  width: 80px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  position: relative;
`;

const ShowResultsTooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  padding: 6px 10px;
  border-radius: 4px;
  background: rgba(0, 27, 59, 0.94);
  color: #fff;
  font-family: 'Mukta Vaani';
  font-size: 12px;
  line-height: 1.4;
  width: 200px;
  text-align: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 100ms ease;
  z-index: 10;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: rgba(0, 27, 59, 0.94);
  }
`;

const ShowResultsHoverGroup = styled.div`
  position: relative;
  display: inline-flex;

  &:hover ${ShowResultsTooltip} {
    opacity: 1;
  }
`;

const RoundShowResultsLabel = styled.label`
  display: inline-block;
  position: relative;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  user-select: none;
  opacity: ${p => p.disabled ? 0.5 : 1};
`;

const RoundShowResultsSlider = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  background-color: #D5D9DD;
  color: rgba(0, 27, 59, 0.6);
  border-radius: 50px;
  transition: background-color 0.3s, color 0.3s;
  font-weight: 600;
  font-size: 10px;
  line-height: 17px;
  width: 40px;
  height: 16px;
  padding: 0 5px;
  box-sizing: border-box;

  &:before {
    content: '';
    position: absolute;
    left: 2px;
    top: 2px;
    width: 14px;
    height: 12px;
    background: linear-gradient(180deg, #FFFFFF 0%, #E8EAEA 100%);
    border-radius: 50%;
    transition: transform 0.3s;
    box-shadow: -2px 1px 6px 0px rgba(0, 0, 0, 0.25);
  }
`;

const RoundShowResultsInput = styled.input`
  display: none;

  &:checked + ${RoundShowResultsSlider} {
    background-color: #2DA548;
    color: #fff;
    justify-content: flex-start;
  }

  &:checked + ${RoundShowResultsSlider}::before {
    transform: translateX(21px);
  }
`;


class TeamsRolesTable extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      defaultRounds: [1, 2, 3]
    };
  }

  render() {
    const {
      rounds,
      onChange,
      onGradingWeightChange,
      changedRounds,
      onUpdate,
      loadingButtonRound,
      defaultActiveRounds,
      onShowResultsChange,
      isIAS,
    } = this.props;

    const { defaultRounds } = this.state;
    return (
      <Container>
        <HeaderRow>
          <HeaderSpacer />
          <HeaderLabel>Start</HeaderLabel>
          <HeaderLabel>End</HeaderLabel>
          <HeaderRoundWeight>Round Weight</HeaderRoundWeight>
          <HeaderUpdateSpacer />
          <HeaderShowResults>Show Results?</HeaderShowResults>
        </HeaderRow>
        {
          rounds.map((round, index) => {
            const isActiveDefault = defaultActiveRounds.includes(round.id);
            const isChanged = changedRounds.includes(round.id);

            // IAS cannot edit any round field — treat them as disabled
            // across start/end pickers, round weight, show-results toggle,
            // and the Update button regardless of changedRounds state.
            const isDisabled = round.disabled === true || isIAS;
            const isUpdateButtonActive = !isDisabled && (isActiveDefault ? true : isChanged);
            const isUpdateButtonLoading = loadingButtonRound === round.id;
            const updateButtonText = isActiveDefault
              ? 'Activate'
              : isChanged
                ? 'Update'
                : 'Updated';

            return (
              <Row key={index}>
                <RoundNameCell><RoundName>{round.displayName || `Round ${round.number}`}</RoundName></RoundNameCell>
                <RoundDateTimeCell disabled={isDisabled}>
                  <DateTimePicker
                    dateFormat="ddd, MMM DD YYYY,"
                    timeFormat="hh:mm a"
                    inputProps={{
                      placeholder: "Select Date & Time",
                      className: "instructor-round-datetime-selector",
                      disabled: isDisabled,
                    }}
                    value={round.start_date}
                    onChange={
                      dt => {
                        if (!isDisabled) onChange(dt, round.id, "start_date");
                      }
                    }
                  />
                  {round.startGapWarning && (
                    <StartGapWarning>{round.startGapWarning}</StartGapWarning>
                  )}
                </RoundDateTimeCell>
                <RoundDateTimeCell disabled={isDisabled}>
                  <DateTimePicker
                    dateFormat="ddd, MMM DD YYYY,"
                    timeFormat="hh:mm a"
                    inputProps={{
                      placeholder: "Select Date & Time",
                      className: "instructor-round-datetime-selector",
                      disabled: isDisabled,
                    }}
                    value={round.end_date}
                    onChange={dt => {
                      if (!isDisabled) onChange(dt, round.id, "end_date");
                    }}
                  />
                </RoundDateTimeCell>
                <RoundWeightCell>
                  {round.displayName === 'Practice' ? (
                    <RoundWeightPlaceholder />
                  ) : (
                    <RoundWeightInput
                      type="number"
                      step="0.0001"
                      min="0"
                      max="1"
                      value={round.grading_weight != null ? round.grading_weight : ''}
                      placeholder="0.3333"
                      disabled={isDisabled}
                      onChange={e => {
                        if (!isDisabled && onGradingWeightChange) {
                          onGradingWeightChange(round.id, e.target.value);
                        }
                      }}
                    />
                  )}
                </RoundWeightCell>
                <RoundUpdateButton
                  isActive={isUpdateButtonActive}
                  isActiveDefault={isActiveDefault}
                  onClick={() => {
                    if (isDisabled) return;
                    let testArray = [...defaultRounds];
                    testArray.splice(round.id - 1, 1);
                    this.setState({
                      defaultRounds: testArray
                    })
                    onUpdate(round.id)
                  }}
                >
                  {
                    isUpdateButtonLoading &&
                    <ButtonLoader>
                      <Loader type="ball-scale-ripple" active />
                    </ButtonLoader>
                  }
                  {
                    !isUpdateButtonLoading &&
                    <span>{updateButtonText}</span>
                  }
                </RoundUpdateButton>
                <RoundShowResultsCell>
                  <ShowResultsHoverGroup>
                    <RoundShowResultsLabel disabled={round.showResultsDisabled || isIAS}>
                      <RoundShowResultsInput
                        type="checkbox"
                        checked={round.showResults}
                        disabled={round.showResultsDisabled || isIAS}
                        onChange={(e) => {
                          if (round.showResultsDisabled || isIAS) return;
                          onShowResultsChange(round.id);
                        }}
                      />
                      <RoundShowResultsSlider>
                        <span>{round.showResults ? 'Yes' : 'No'}</span>
                      </RoundShowResultsSlider>
                    </RoundShowResultsLabel>
                    {round.showResultsDisabled && round.showResultsTooltip && (
                      <ShowResultsTooltip>{round.showResultsTooltip}</ShowResultsTooltip>
                    )}
                  </ShowResultsHoverGroup>
                </RoundShowResultsCell>
              </Row>
            );
          })
        }
      </Container>
    );
  }
}

export default TeamsRolesTable;



// WEBPACK FOOTER //
// ./src/instructor/components/round-admin/table.js